import { normalizeGreek } from '@search/shared';
import { greeklishToGreek, looksLikeGreeklish } from '@/lib/greeklishToGreek';
// NLF agent disabled for now — re-enable by importing shouldTriggerNlfAgent
// import { shouldTriggerNlfAgent } from '@/lib/nlf/nlfTriggerRules';
export const shouldTriggerNLF = (_query: string): boolean => false;

// Stop words for local helpers (isSimpleDescriptiveQuery etc.)
const STOP_WORDS = new Set([
  'ο', 'η', 'το', 'οι', 'τα', 'του', 'της', 'των', 'τον', 'την', 'τους', 'τις',
  'ένα', 'ενα', 'μια', 'μία', 'ένας', 'ενας',
  'και', 'ή', 'για', 'σε', 'με', 'απο', 'από', 'προς', 'στο', 'στη', 'στα', 'στον', 'στην',
  'κατι', 'κατιτι',
  'the', 'a', 'an', 'of', 'in', 'on', 'at', 'to', 'for', 'with',
]);

export function isSimplePriceQuery(query: string): boolean {
  const lowerQuery = query.toLowerCase();

  const hasPricePattern = /(?:μέχρι|μεχρι|έως|εως|κάτω από|κατω απο|κάτω|κατω|πάνω από|πανω απο|πάνω|πανω|under|over|below|above)\s+\d+(\.\d+)?\s*(?:ευρω|ευρώ|€|euros?)?/i.test(query);
  if (!hasPricePattern) return false;

  const complexIntentWords = [
    'δώρο', 'δωρο', 'gift',
    'για ',
    'σετ', 'set',
    'τι να', 'πως να', 'πώς να',
    'ποια', 'ποιο', 'ποιον', 'ποιος',
    'χρειαζομαι', 'χρειάζομαι',
    'θελω', 'θέλω',
    'ψαχνω', 'ψάχνω',
    'καλυτερο', 'καλύτερο', 'καλυτερη', 'καλύτερη',
    'ολοκληρο', 'ολόκληρο', 'ολοκληρωμένο', 'ολοκληρωμενο',
    'όλα για', 'ολα για',
  ];

  const hasComplexIntent = complexIntentWords.some(word => lowerQuery.includes(word));
  return !hasComplexIntent;
}

export function isSimpleDescriptiveQuery(query: string): boolean {
  const lowerQuery = query.toLowerCase();
  const words = query.trim().split(/\s+/);

  const meaningfulWords = words.filter(w => !STOP_WORDS.has(w.toLowerCase()));
  if (meaningfulWords.length !== 2) return false;
  if (meaningfulWords.length === words.length) return false;

  const intentWords = [
    'δώρο', 'δωρο', 'gift',
    'σετ', 'set',
    'όλα', 'ολα',
    'τι', 'πως', 'πώς',
    'χρειάζομαι', 'χρειαζομαι',
    'θέλω', 'θελω',
    'ψάχνω', 'ψαχνω',
    'καλύτερο', 'καλυτερο', 'καλύτερη', 'καλυτερη',
    'χόμπι', 'χομπι', 'αγαπημένο', 'αγαπημενο',
  ];

  const hasIntentWord = intentWords.some(word => lowerQuery.includes(word));
  return !hasIntentWord;
}

export function shouldUseAiFirstFlow(query: string): boolean {
  if (isSimplePriceQuery(query)) {
    console.log('💨 Simple price query detected → using fast rule-based search');
    return false;
  }
  if (isSimpleDescriptiveQuery(query)) {
    console.log('💨 Simple descriptive query detected → using normal search');
    return false;
  }
  return shouldTriggerNLF(query);
}

export function findRelevantCategories(
  query: string,
  categoryFacets: Record<string, number> | undefined,
): { name: string; count: number }[] {
  if (!categoryFacets || !query.trim()) {
    return [];
  }

  const normalizedQuery = normalizeGreek(query);
  const queryTokens = new Set(normalizedQuery.split(/\s+/).filter(t => t.length >= 3));

  // Greeklish bridge: a Latin-typed query ("kamera") won't match Greek category
  // names ("Κάμερες") because normalizeGreek doesn't transliterate Latin→Greek.
  // Mirror the main search route — also derive tokens from the transliterated
  // form so the "Προτεινόμενες Κατηγορίες" group works for Greeklish queries.
  if (looksLikeGreeklish(query)) {
    const transliterated = normalizeGreek(greeklishToGreek(query));
    for (const t of transliterated.split(/\s+/)) {
      if (t.length >= 3) queryTokens.add(t);
    }
  }

  if (queryTokens.size === 0) return [];

  const matched: { name: string; count: number; score: number }[] = [];

  Object.entries(categoryFacets).forEach(([catName, count]) => {
    const normalizedCat = normalizeGreek(catName);
    const catWords = normalizedCat.split(/[\s/&]+/).filter(w => w.length > 0);

    let score = 0;
    for (const token of queryTokens) {
      const trimmed = token.length >= 4 ? token.slice(0, -1) : token;
      if (catWords.some(word => word.startsWith(token) || word.startsWith(trimmed))) {
        score += token.length;
      }
    }

    if (score > 0) {
      matched.push({ name: catName, count, score });
    }
  });

  matched.sort((a, b) => b.score - a.score || b.count - a.count);
  return matched.map(({ name, count }) => ({ name, count }));
}

/** The fixed price-band table — single source for the sidebar band group AND
 *  chip labels (range key ↔ display label). */
export const PRICE_BAND_DEFS = [
  { label: 'Under €20', min: 0, max: 20, range: '0-20' },
  { label: '€20 - €40', min: 20, max: 40, range: '20-40' },
  { label: '€40 - €60', min: 40, max: 60, range: '40-60' },
  { label: '€60 - €100', min: 60, max: 100, range: '60-100' },
  { label: '€100 - €150', min: 100, max: 150, range: '100-150' },
  { label: 'Over €150', min: 150, max: Infinity, range: '150+' },
] as const;

/** Display label for a band range key ("20-40" → "€20 - €40"); null if unknown. */
export function priceBandLabel(range: string): string | null {
  return PRICE_BAND_DEFS.find(b => b.range === range)?.label ?? null;
}

export function groupIntoPriceBands(
  priceFacets: Record<string, number> | undefined
): { label: string; count: number; range: string }[] {
  if (!priceFacets) {
    return [];
  }

  const bandCounts = PRICE_BAND_DEFS.map(band => ({ ...band, count: 0 }));

  Object.entries(priceFacets).forEach(([priceStr, count]) => {
    const price = parseFloat(priceStr);
    if (isNaN(price)) return;
    const bandIndex = bandCounts.findIndex(band => price >= band.min && price < band.max);
    if (bandIndex !== -1) {
      bandCounts[bandIndex].count += count;
    }
  });

  return bandCounts.filter(band => band.count > 0);
}
